import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const TIME_SLOTS = (() => {
  const slots = [];
  for (let h = 6; h <= 21; h++) {
    for (const m of [0, 30]) {
      if (h === 21 && m === 30) break;
      const period = h < 12 ? 'AM' : 'PM';
      const hour12 = h % 12 === 0 ? 12 : h % 12;
      slots.push(`${hour12}:${m === 0 ? '00' : '30'} ${period}`);
    }
  }
  return slots;
})();
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Trash2, Pencil, ChevronDown, ChevronRight, Users, ArrowRightLeft, UserMinus, Download, CalendarDays, Plus, CheckCircle2, XCircle } from 'lucide-react';

export default function AdminCohortsPage() {
  const navigate = useNavigate();
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Expanded cohort students
  const [expandedCohort, setExpandedCohort] = useState(null);
  const [cohortTab, setCohortTab] = useState({}); // { cohort_id: 'students' | 'sessions' }
  const [cohortStudents, setCohortStudents] = useState({});
  const [loadingStudents, setLoadingStudents] = useState(null);

  // Sessions
  const [cohortSessions, setCohortSessions] = useState({});
  const [loadingSessions, setLoadingSessions] = useState(null);
  const [sessionDialog, setSessionDialog] = useState(false);
  const [sessionCohortId, setSessionCohortId] = useState(null);
  const [sessionForm, setSessionForm] = useState({ date: '', label: '' });
  const [savingSession, setSavingSession] = useState(false);

  // Attendance
  const [todaySessions, setTodaySessions] = useState({});
  const [todayAttendance, setTodayAttendance] = useState({});
  const [markingAttendance, setMarkingAttendance] = useState({});
  const [sessionAttendance, setSessionAttendance] = useState({});
  const [expandedSessionId, setExpandedSessionId] = useState(null);

  // Attendance register
  const [register, setRegister] = useState({}); // { cohort_id: { loading, data } }

  // New cohort
  const [newDialog, setNewDialog] = useState(false);
  const [newForm, setNewForm] = useState({ name: '', start_date: '', end_date: '', whatsapp_link: '', start_time: '', acceptance_deadline: '' });

  // Edit cohort
  const [editDialog, setEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({ id: '', whatsapp_link: '', start_time: '', acceptance_deadline: '' });

  const [moveDialog, setMoveDialog] = useState(false);
  const [moveStudent, setMoveStudent] = useState(null);
  const [moveTargetCohort, setMoveTargetCohort] = useState('');
  const [moving, setMoving] = useState(false);

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const downloadCsv = async (url, filename) => {
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error('Export failed');
    }
  };

  useEffect(() => { fetchCohorts(); }, []);

  const fetchCohorts = async () => {
    try {
      const res = await axios.get(`${API}/cohorts`, auth());
      const list = res.data;
      setCohorts(list);
      // Auto-expand first cohort
      if (list.length > 0) {
        const first = list[0];
        setExpandedCohort(first.id);
        const stuRes = await axios.get(`${API}/students/cohort/${first.id}`, auth());
        const sorted = [...stuRes.data].sort((a, b) => a.full_name.localeCompare(b.full_name));
        setCohortStudents(prev => ({ ...prev, [first.id]: sorted }));
      }
    } catch {
      toast.error('Failed to load cohorts');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCohort = async (cohortId) => {
    if (expandedCohort === cohortId) {
      setExpandedCohort(null);
      return;
    }
    setExpandedCohort(cohortId);
    if (cohortStudents[cohortId]) return; // already loaded
    setLoadingStudents(cohortId);
    try {
      const res = await axios.get(`${API}/students/cohort/${cohortId}`, auth());
      const sorted = [...res.data].sort((a, b) => a.full_name.localeCompare(b.full_name));
      setCohortStudents(prev => ({ ...prev, [cohortId]: sorted }));
    } catch {
      toast.error('Failed to load students');
    } finally {
      setLoadingStudents(null);
    }
  };

  const handleMarkAttendance = async (cohortId, studentId, currentPresent) => {
    const key = `${cohortId}-${studentId}`;
    const newPresent = !currentPresent;
    setMarkingAttendance(prev => ({ ...prev, [key]: true }));
    setTodayAttendance(prev => ({
      ...prev,
      [cohortId]: { ...prev[cohortId], [studentId]: newPresent },
    }));
    try {
      // Lazy-create today's session on the first mark of the day
      let sessionId = todaySessions[cohortId]?.id;
      if (!sessionId) {
        const res = await axios.get(`${API}/cohorts/${cohortId}/sessions/today`, auth());
        sessionId = res.data.session.id;
        setTodaySessions(prev => ({ ...prev, [cohortId]: res.data.session }));
        setTodayAttendance(prev => ({ ...prev, [cohortId]: { ...res.data.attendance, [studentId]: newPresent } }));
      }
      await axios.patch(`${API}/sessions/${sessionId}/attendance/${studentId}`, { present: newPresent }, auth());
    } catch {
      setTodayAttendance(prev => ({
        ...prev,
        [cohortId]: { ...prev[cohortId], [studentId]: currentPresent },
      }));
      toast.error('Failed to update attendance');
    } finally {
      setMarkingAttendance(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleToggleSessionAttendance = async (sessionId) => {
    if (expandedSessionId === sessionId) { setExpandedSessionId(null); return; }
    setExpandedSessionId(sessionId);
    setSessionAttendance(prev => ({ ...prev, [sessionId]: { loading: true, data: prev[sessionId]?.data || null } }));
    try {
      const res = await axios.get(`${API}/sessions/${sessionId}/attendance`, auth());
      setSessionAttendance(prev => ({ ...prev, [sessionId]: { loading: false, data: res.data } }));
    } catch {
      setSessionAttendance(prev => ({ ...prev, [sessionId]: { loading: false, data: null } }));
      toast.error('Failed to load attendance');
    }
  };

  const handleMarkSessionAttendance = async (sessionId, studentId, currentPresent) => {
    const newPresent = !currentPresent;
    setSessionAttendance(prev => ({
      ...prev,
      [sessionId]: {
        ...prev[sessionId],
        data: {
          ...prev[sessionId].data,
          attendance: prev[sessionId].data.attendance.map(a =>
            a.student_id === studentId ? { ...a, present: newPresent } : a
          ),
        },
      },
    }));
    try {
      await axios.patch(`${API}/sessions/${sessionId}/attendance/${studentId}`, { present: newPresent }, auth());
    } catch {
      setSessionAttendance(prev => ({
        ...prev,
        [sessionId]: {
          ...prev[sessionId],
          data: {
            ...prev[sessionId].data,
            attendance: prev[sessionId].data.attendance.map(a =>
              a.student_id === studentId ? { ...a, present: currentPresent } : a
            ),
          },
        },
      }));
      toast.error('Failed to update attendance');
    }
  };

  const handleLoadSessions = async (cohortId) => {
    if (cohortSessions[cohortId]) return;
    setLoadingSessions(cohortId);
    try {
      const res = await axios.get(`${API}/cohorts/${cohortId}/sessions`, auth());
      setCohortSessions(prev => ({ ...prev, [cohortId]: res.data }));
    } catch {
      toast.error('Failed to load sessions');
    } finally {
      setLoadingSessions(null);
    }
  };

  const handleTabSwitch = (cohortId, tab) => {
    setCohortTab(prev => ({ ...prev, [cohortId]: tab }));
    if (tab === 'sessions') handleLoadSessions(cohortId);
    if (tab === 'attendance') handleLoadRegister(cohortId);
  };

  const handleLoadRegister = async (cohortId) => {
    setRegister(prev => ({ ...prev, [cohortId]: { loading: true, data: prev[cohortId]?.data || null } }));
    try {
      const res = await axios.get(`${API}/cohorts/${cohortId}/attendance-register`, auth());
      setRegister(prev => ({ ...prev, [cohortId]: { loading: false, data: res.data } }));
    } catch {
      toast.error('Failed to load attendance register');
      setRegister(prev => ({ ...prev, [cohortId]: { loading: false, data: null } }));
    }
  };

  const handleDownloadRegisterCsv = async (cohortId, cohortName) => {
    try {
      const res = await fetch(`${API}/cohorts/${cohortId}/attendance-register/csv`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${cohortName.replace(/\s+/g, '_')}_attendance.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error('Failed to download CSV');
    }
  };

  const handleDownloadCertificateReport = async (cohortId, cohortName) => {
    try {
      const res = await fetch(`${API}/cohorts/${cohortId}/certificate-report/csv`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${cohortName.replace(/\s+/g, '_')}_certificate_report.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error('Failed to download certificate report');
    }
  };

  const handleCreateSession = async () => {
    if (!sessionForm.date || !sessionForm.label.trim()) {
      toast.error('Date and label are required');
      return;
    }
    setSavingSession(true);
    try {
      const res = await axios.post(
        `${API}/cohorts/${sessionCohortId}/sessions`,
        { date: sessionForm.date, label: sessionForm.label },
        auth(),
      );
      setCohortSessions(prev => ({
        ...prev,
        [sessionCohortId]: [...(prev[sessionCohortId] || []), res.data].sort((a, b) => a.date.localeCompare(b.date)),
      }));
      toast.success('Session added');
      setSessionDialog(false);
      setSessionForm({ date: '', label: '' });
    } catch {
      toast.error('Failed to add session');
    } finally {
      setSavingSession(false);
    }
  };

  const handleDeleteSession = async (cohortId, sessionId) => {
    if (!window.confirm('Delete this session? Attendance records for this session will also be deleted.')) return;
    try {
      await axios.delete(`${API}/cohorts/${cohortId}/sessions/${sessionId}`, auth());
      setCohortSessions(prev => ({
        ...prev,
        [cohortId]: prev[cohortId].filter(s => s.id !== sessionId),
      }));
      toast.success('Session deleted');
    } catch {
      toast.error('Failed to delete session');
    }
  };

  const handleCreate = async () => {
    if (!newForm.name || !newForm.start_date || !newForm.end_date) {
      toast.error('Name, start date and end date are required');
      return;
    }
    try {
      await axios.post(`${API}/cohorts`, newForm, auth());
      toast.success('Cohort created');
      setNewDialog(false);
      setNewForm({ name: '', start_date: '', end_date: '', whatsapp_link: '', start_time: '', acceptance_deadline: '' });
      fetchCohorts();
    } catch {
      toast.error('Failed to create cohort');
    }
  };

  const handleDelete = async (cohortId) => {
    if (!window.confirm('Are you sure you want to delete this cohort? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/cohorts/${cohortId}`, auth());
      toast.success('Cohort deleted');
      fetchCohorts();
    } catch {
      toast.error('Failed to delete cohort');
    }
  };

  const handleRemoveStudent = async (student, cohortId) => {
    if (!window.confirm(`Remove ${student.full_name} from this cohort? This will delete their student record.`)) return;
    try {
      await axios.delete(`${API}/students/${student.id}`, auth());
      toast.success(`${student.full_name} removed`);
      setCohortStudents(prev => ({
        ...prev,
        [cohortId]: prev[cohortId].filter(s => s.id !== student.id),
      }));
    } catch {
      toast.error('Failed to remove student');
    }
  };

  const handleMoveStudent = async () => {
    if (!moveTargetCohort || moveTargetCohort === moveStudent?.cohort_id) {
      toast.error('Select a different cohort');
      return;
    }
    setMoving(true);
    try {
      await axios.patch(`${API}/students/${moveStudent.id}/cohort?cohort_id=${moveTargetCohort}`, {}, auth());
      toast.success(`${moveStudent.full_name} moved successfully`);
      setMoveDialog(false);
      setCohortStudents(prev => {
        const updated = { ...prev };
        if (updated[moveStudent.cohort_id]) {
          updated[moveStudent.cohort_id] = updated[moveStudent.cohort_id].filter(s => s.id !== moveStudent.id);
        }
        delete updated[moveTargetCohort];
        return updated;
      });
    } catch {
      toast.error('Failed to move student');
    } finally {
      setMoving(false);
    }
  };

  const handleUpdateCohort = async () => {
    try {
      const params = new URLSearchParams();
      if (editForm.whatsapp_link !== undefined) params.set('whatsapp_link', editForm.whatsapp_link);
      if (editForm.start_time !== undefined) params.set('start_time', editForm.start_time);
      if (editForm.acceptance_deadline !== undefined) params.set('acceptance_deadline', editForm.acceptance_deadline);
      await axios.patch(`${API}/cohorts/${editForm.id}?${params.toString()}`, {}, auth());
      toast.success('Cohort updated');
      setEditDialog(false);
      fetchCohorts();
    } catch {
      toast.error('Failed to update cohort');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">Loading…</div>;

  return (
    <div>
      {/* Page header */}
      <div className="bg-white border-b px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-playfair font-bold text-primary">Cohorts</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Manage class groups for enrolled students</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => downloadCsv(`${API}/export/cohorts`, 'cohorts.csv')}>
            <Download className="w-3 h-3 mr-1" /> Export CSV
          </Button>
          <Button onClick={() => setNewDialog(true)}>+ New Cohort</Button>
        </div>
      </div>

      <div className="p-6 space-y-4">
        {cohorts.length === 0 && (
          <Card>
            <CardContent className="py-10 text-center text-muted-foreground">
              No cohorts yet. Create your first one.
            </CardContent>
          </Card>
        )}

        {cohorts.map((cohort) => {
          const isExpanded = expandedCohort === cohort.id;
          const students = cohortStudents[cohort.id] || [];
          const isLoadingThis = loadingStudents === cohort.id;

          return (
            <Card key={cohort.id} className={isExpanded ? 'border-primary/30' : ''}>
              {/* Cohort header row */}
              <div
                className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-muted/30 rounded-t-lg"
                onClick={() => handleToggleCohort(cohort.id)}
              >
                <div className="flex items-center gap-3">
                  {isExpanded
                    ? <ChevronDown className="w-4 h-4 text-muted-foreground" />
                    : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                  <div>
                    <span className="font-semibold text-primary">{cohort.name}</span>
                    <span className="text-sm text-muted-foreground ml-3">{cohort.start_date} → {cohort.end_date}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3" onClick={e => e.stopPropagation()}>
                  <span className="flex items-center gap-1 text-sm text-muted-foreground">
                    <Users className="w-4 h-4" />
                    {cohortStudents[cohort.id] ? cohortStudents[cohort.id].length : '—'}
                  </span>
                  {cohort.active
                    ? <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700 font-medium">Active</span>
                    : <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-500">Inactive</span>}
                  {cohort.whatsapp_link && (
                    <a href={cohort.whatsapp_link} target="_blank" rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:underline">WhatsApp</a>
                  )}
                  <Button size="sm" variant="outline"
                    onClick={() => { setEditForm({ id: cohort.id, whatsapp_link: cohort.whatsapp_link || '', start_time: cohort.start_time || '', acceptance_deadline: cohort.acceptance_deadline || '' }); setEditDialog(true); }}>
                    <Pencil className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => handleDelete(cohort.id)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>

              {/* Expanded section */}
              {isExpanded && (
                <CardContent className="pt-0 pb-4 border-t">
                  {/* Sub-tabs */}
                  <div className="flex gap-1 border-b mb-4 mt-2">
                    {['students', 'sessions', 'attendance'].map(t => (
                      <button key={t} onClick={() => handleTabSwitch(cohort.id, t)}
                        className={`px-4 py-2 text-sm font-medium border-b-2 capitalize transition-colors ${
                          (cohortTab[cohort.id] || 'students') === t
                            ? 'border-primary text-primary'
                            : 'border-transparent text-muted-foreground hover:text-foreground'
                        }`}>
                        {t === 'students'
                          ? <><Users className="w-3.5 h-3.5 inline mr-1.5" />Students</>
                          : t === 'sessions'
                          ? <><CalendarDays className="w-3.5 h-3.5 inline mr-1.5" />Sessions</>
                          : <><Download className="w-3.5 h-3.5 inline mr-1.5" />Attendance</>}
                      </button>
                    ))}
                  </div>

                  {/* Students tab */}
                  {(cohortTab[cohort.id] || 'students') === 'students' && (
                    isLoadingThis ? (
                      <div className="text-center text-sm text-muted-foreground py-6">Loading students…</div>
                    ) : students.length === 0 ? (
                      <div className="text-center text-sm text-muted-foreground py-6">No students enrolled in this cohort yet.</div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>#</TableHead>
                            <TableHead>Full Name</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Enrolled</TableHead>
                            <TableHead>
                              <div>
                                <div>Today</div>
                                {todaySessions[cohort.id] && (
                                  <div className="text-xs font-normal text-muted-foreground">{todaySessions[cohort.id].label}</div>
                                )}
                              </div>
                            </TableHead>
                            <TableHead>Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {students.map((s, idx) => {
                            const isPresent = todayAttendance[cohort.id]?.[s.id] === true;
                            const isMarking = markingAttendance[`${cohort.id}-${s.id}`];
                            return (
                              <TableRow key={s.id} className="cursor-pointer hover:bg-blue-50/40 group"
                                onClick={() => navigate(`/admin/students/${s.id}`, { state: { from: '/admin/cohorts' } })}>
                                <TableCell className="text-muted-foreground text-sm">{idx + 1}</TableCell>
                                <TableCell className="font-medium group-hover:text-primary transition-colors">{s.full_name}</TableCell>
                                <TableCell className="text-muted-foreground">{s.email}</TableCell>
                                <TableCell className="text-muted-foreground text-sm">
                                  {s.enrollment_date ? new Date(s.enrollment_date).toLocaleDateString() : '—'}
                                </TableCell>
                                <TableCell onClick={e => e.stopPropagation()}>
                                  <button
                                    type="button"
                                    disabled={isMarking}
                                    onClick={() => handleMarkAttendance(cohort.id, s.id, isPresent)}
                                    className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors disabled:opacity-50 ${
                                      isPresent
                                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                    }`}
                                  >
                                    {isPresent
                                      ? <CheckCircle2 className="w-3.5 h-3.5" />
                                      : <XCircle className="w-3.5 h-3.5" />}
                                    {isPresent ? 'Present' : 'Absent'}
                                  </button>
                                </TableCell>
                                <TableCell onClick={e => e.stopPropagation()}>
                                  <div className="flex items-center gap-2">
                                    <button type="button" title="Move to another cohort" className="text-blue-500 hover:text-blue-700 p-0.5"
                                      onClick={() => { setMoveStudent({ id: s.id, full_name: s.full_name, cohort_id: cohort.id }); setMoveTargetCohort(''); setMoveDialog(true); }}>
                                      <ArrowRightLeft className="w-3.5 h-3.5" />
                                    </button>
                                    <button type="button" title="Remove from cohort" className="text-red-400 hover:text-red-600 p-0.5"
                                      onClick={() => handleRemoveStudent(s, cohort.id)}>
                                      <UserMinus className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    )
                  )}

                  {/* Attendance Register tab */}
                  {cohortTab[cohort.id] === 'attendance' && (() => {
                    const reg = register[cohort.id];
                    const data = reg?.data;
                    return (
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm text-muted-foreground">
                            {data ? `${data.sessions.length} sessions · ${data.students.length} students` : ''}
                          </span>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => handleDownloadRegisterCsv(cohort.id, cohort.name)}>
                              <Download className="w-3.5 h-3.5 mr-1" /> Full Register
                            </Button>
                            <Button size="sm" variant="outline" className="border-green-200 text-green-700 hover:bg-green-50" onClick={() => handleDownloadCertificateReport(cohort.id, cohort.name)}>
                              <Download className="w-3.5 h-3.5 mr-1" /> Certificate Report
                            </Button>
                          </div>
                        </div>

                        {reg?.loading ? (
                          <div className="text-center text-sm text-muted-foreground py-8">Loading register…</div>
                        ) : !data ? (
                          <div className="text-center text-sm text-muted-foreground py-8">No data.</div>
                        ) : data.sessions.length === 0 ? (
                          <div className="text-center text-sm text-muted-foreground py-8">No sessions recorded yet.</div>
                        ) : (
                          <div className="overflow-x-auto rounded border">
                            <table className="text-xs w-full border-collapse">
                              <thead>
                                <tr className="bg-muted/50">
                                  <th className="text-left px-3 py-2 font-medium sticky left-0 bg-muted/50 border-r min-w-[180px]">Student</th>
                                  {data.sessions.map(s => (
                                    <th key={s.id} className="px-2 py-2 font-medium text-center whitespace-nowrap border-r">
                                      {new Date(s.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                                    </th>
                                  ))}
                                  <th className="px-3 py-2 font-medium text-center bg-muted/50 sticky right-0 border-l min-w-[90px]">Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                {data.students.map((stu, idx) => {
                                  const pct = stu.total_sessions > 0
                                    ? Math.round((stu.total_present / stu.total_sessions) * 100)
                                    : null;
                                  return (
                                    <tr key={stu.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-muted/20'}>
                                      <td className={`px-3 py-1.5 font-medium sticky left-0 border-r ${idx % 2 === 0 ? 'bg-white' : 'bg-muted/20'}`}>
                                        {stu.full_name}
                                      </td>
                                      {data.sessions.map(s => {
                                        const val = stu.attendance[s.id];
                                        return (
                                          <td key={s.id} className="text-center border-r py-1.5">
                                            {val === true
                                              ? <span className="inline-block w-5 h-5 rounded-full bg-green-500 text-white text-[10px] font-bold leading-5">P</span>
                                              : val === false
                                              ? <span className="inline-block w-5 h-5 rounded-full bg-red-100 text-red-500 text-[10px] font-bold leading-5">A</span>
                                              : <span className="text-gray-300">—</span>}
                                          </td>
                                        );
                                      })}
                                      <td className="text-center px-3 py-1.5 sticky right-0 border-l bg-inherit">
                                        <span className={`font-semibold ${pct >= 80 ? 'text-green-700' : pct >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                                          {stu.total_present}/{stu.total_sessions}
                                        </span>
                                        <span className="text-muted-foreground ml-1">({pct ?? '—'}%)</span>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                              <tfoot>
                                <tr className="bg-muted/50 font-medium border-t-2">
                                  <td className="px-3 py-2 sticky left-0 bg-muted/50 border-r text-xs text-muted-foreground">Daily rate</td>
                                  {data.sessions.map(s => {
                                    const presentCount = data.students.filter(stu => stu.attendance[s.id] === true).length;
                                    const total = data.students.length;
                                    const pct = total > 0 ? Math.round((presentCount / total) * 100) : 0;
                                    return (
                                      <td key={s.id} className="text-center border-r text-xs py-2">
                                        <div className={`font-semibold ${pct >= 80 ? 'text-green-700' : pct >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>{pct}%</div>
                                        <div className="text-muted-foreground">{presentCount}/{total}</div>
                                      </td>
                                    );
                                  })}
                                  <td className="sticky right-0 bg-muted/50 border-l" />
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Sessions tab */}
                  {cohortTab[cohort.id] === 'sessions' && (
                    <div>
                      <div className="flex justify-end mb-3">
                        <Button size="sm" onClick={() => { setSessionCohortId(cohort.id); setSessionForm({ date: '', label: '' }); setSessionDialog(true); }}>
                          <Plus className="w-3.5 h-3.5 mr-1" /> Add Session
                        </Button>
                      </div>
                      {loadingSessions === cohort.id ? (
                        <div className="text-center text-sm text-muted-foreground py-6">Loading sessions…</div>
                      ) : (cohortSessions[cohort.id] || []).length === 0 ? (
                        <div className="text-center text-sm text-muted-foreground py-6">No sessions yet. Add the first one.</div>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>#</TableHead>
                              <TableHead>Date</TableHead>
                              <TableHead>Label</TableHead>
                              <TableHead>Created By</TableHead>
                              <TableHead></TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {(cohortSessions[cohort.id] || []).map((session, idx) => {
                              const isExpSess = expandedSessionId === session.id;
                              const sessAtt = sessionAttendance[session.id];
                              return (
                                <React.Fragment key={session.id}>
                                  <TableRow>
                                    <TableCell className="text-muted-foreground text-sm">{idx + 1}</TableCell>
                                    <TableCell className="text-sm font-medium">
                                      {new Date(session.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                                    </TableCell>
                                    <TableCell className="text-sm">{session.label}</TableCell>
                                    <TableCell className="text-sm text-muted-foreground">{session.created_by || '—'}</TableCell>
                                    <TableCell>
                                      <div className="flex items-center gap-2">
                                        <button type="button" title="View / edit attendance"
                                          className="text-blue-500 hover:text-blue-700 p-0.5"
                                          onClick={() => handleToggleSessionAttendance(session.id)}>
                                          {isExpSess ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                                        </button>
                                        <button type="button" className="text-red-400 hover:text-red-600 p-0.5"
                                          onClick={() => handleDeleteSession(cohort.id, session.id)}>
                                          <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                  {isExpSess && (
                                    <TableRow>
                                      <TableCell colSpan={5} className="bg-muted/30 px-4 py-3">
                                        {sessAtt?.loading ? (
                                          <p className="text-sm text-muted-foreground text-center py-2">Loading attendance…</p>
                                        ) : sessAtt?.data ? (
                                          <div>
                                            <div className="flex items-center justify-between mb-2">
                                              <span className="text-xs font-medium text-muted-foreground">
                                                {sessAtt.data.attendance.filter(a => a.present === true).length} / {sessAtt.data.attendance.length} present
                                              </span>
                                            </div>
                                            <div className="grid grid-cols-2 gap-1.5">
                                              {sessAtt.data.attendance.map(a => (
                                                <div key={a.student_id} className="flex items-center justify-between bg-white border rounded px-3 py-1.5 text-sm">
                                                  <span className="truncate mr-2">{a.full_name}</span>
                                                  <button
                                                    type="button"
                                                    onClick={() => handleMarkSessionAttendance(session.id, a.student_id, a.present === true)}
                                                    className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium shrink-0 transition-colors ${
                                                      a.present === true
                                                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                                    }`}
                                                  >
                                                    {a.present === true
                                                      ? <CheckCircle2 className="w-3 h-3" />
                                                      : <XCircle className="w-3 h-3" />}
                                                    {a.present === true ? 'Present' : 'Absent'}
                                                  </button>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        ) : (
                                          <p className="text-sm text-muted-foreground text-center py-2">No data</p>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </TableBody>
                        </Table>
                      )}
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      {/* New Cohort Dialog */}
      <Dialog open={newDialog} onOpenChange={setNewDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Cohort</DialogTitle>
            <DialogDescription>Set up a new class cohort for enrolled students.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Cohort Name *</Label>
              <Input placeholder="e.g. Cohort 4 – June 2026" value={newForm.name}
                onChange={e => setNewForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date *</Label>
                <Input type="date" value={newForm.start_date}
                  onChange={e => setNewForm(f => ({ ...f, start_date: e.target.value }))} />
              </div>
              <div>
                <Label>End Date *</Label>
                <Input type="date" value={newForm.end_date}
                  onChange={e => setNewForm(f => ({ ...f, end_date: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Time</Label>
                <select value={newForm.start_time} onChange={e => setNewForm(f => ({ ...f, start_time: e.target.value }))}
                  className="w-full border border-input rounded-md px-3 py-2 text-sm bg-background">
                  <option value="">— Select time —</option>
                  {TIME_SLOTS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <Label>Acceptance Deadline</Label>
                <Input type="date" value={newForm.acceptance_deadline}
                  onChange={e => setNewForm(f => ({ ...f, acceptance_deadline: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label>WhatsApp Group Link</Label>
              <Input placeholder="https://chat.whatsapp.com/…" value={newForm.whatsapp_link}
                onChange={e => setNewForm(f => ({ ...f, whatsapp_link: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate}>Create Cohort</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Cohort Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Cohort</DialogTitle>
            <DialogDescription>Update cohort details used in acceptance emails.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Time</Label>
                <select value={editForm.start_time} onChange={e => setEditForm(f => ({ ...f, start_time: e.target.value }))}
                  className="w-full border border-input rounded-md px-3 py-2 text-sm bg-background">
                  <option value="">— Select time —</option>
                  {TIME_SLOTS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <Label>Acceptance Deadline</Label>
                <Input type="date" value={editForm.acceptance_deadline}
                  onChange={e => setEditForm(f => ({ ...f, acceptance_deadline: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label>WhatsApp Group Link</Label>
              <Input placeholder="https://chat.whatsapp.com/…" value={editForm.whatsapp_link}
                onChange={e => setEditForm(f => ({ ...f, whatsapp_link: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateCohort}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Session Dialog */}
      <Dialog open={sessionDialog} onOpenChange={setSessionDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add Session</DialogTitle>
            <DialogDescription>Record a class session for attendance tracking.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Date *</Label>
              <Input type="date" value={sessionForm.date}
                onChange={e => setSessionForm(f => ({ ...f, date: e.target.value }))} />
            </div>
            <div>
              <Label>Session Label *</Label>
              <Input placeholder="e.g. Introduction to Civil Procedure"
                value={sessionForm.label}
                onChange={e => setSessionForm(f => ({ ...f, label: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSessionDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateSession} disabled={savingSession}>
              {savingSession ? 'Saving…' : 'Add Session'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move Student Dialog */}
      <Dialog open={moveDialog} onOpenChange={setMoveDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Move Student to Another Cohort</DialogTitle>
            <DialogDescription>
              Moving <strong>{moveStudent?.full_name}</strong> to a new cohort.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label>Select Cohort</Label>
            <select
              className="w-full border rounded px-3 py-2 text-sm mt-1"
              value={moveTargetCohort}
              onChange={e => setMoveTargetCohort(e.target.value)}
            >
              <option value="">— Select cohort —</option>
              {cohorts
                .filter(c => c.id !== moveStudent?.cohort_id)
                .map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveDialog(false)}>Cancel</Button>
            <Button onClick={handleMoveStudent} disabled={moving || !moveTargetCohort}>
              {moving ? 'Moving…' : 'Move Student'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
